import { useObjectMemo } from '@client/hooks/use-object.hooks';
import { ArrowDropDown, Warning } from '@mui/icons-material';
import {
  Collapse,
  IconButton,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  FormatInput,
  toDisplayUnit,
} from '@shared/helpers/display/get-display-value-data.helper';
import { guards } from '@shared/helpers/typeguards.helpers';
import React, { useMemo } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useToggle } from 'usehooks-ts';
import ExpressionTypography from '@features/expressions/ExpressionTypography';
import { isQuantityUnit } from '@shared/helpers/unit.helpers';
import { QuantityUnit } from '@shared/models/unit.model';

type INodonListItemResultProps = {
  name: string;
  value?: number | string;
  unit?: FormatInput['unit'] | QuantityUnit;
  indentation?: number;
  highlight?: boolean;
  tooltip?: string;
  marginTop?: number; // Can propbably clean this up with a better name
  children?: React.ReactNode;
  /**
   * Provide an error message for the field to highlight errors
   */
  error?: string;
};

/**
 * A list item with a name and a value (and unit).
 * Can optionally expand to show children.
 * @param param0
 * @returns
 */
export const NodonListItemResult: React.FC<INodonListItemResultProps> = ({
  name,
  value,
  unit,
  indentation = 0,
  highlight = false,
  tooltip,
  marginTop,
  children,
  error,
}) => {
  const { classes } = useStyles();
  const [isExpanded, toggleExpanded] = useToggle(false);

  // Determine unit and displayUnit props for ExpressionTypography
  const expressionTypographyUnit = useMemo(() => {
    if (!guards.is.number(value)) return undefined;
    return isQuantityUnit(unit) ? unit : 'none';
  }, [value, unit]);

  const expressionTypographyDisplayUnit = useMemo(() => {
    if (!guards.is.number(value)) return undefined;
    if (!unit) return undefined;
    return isQuantityUnit(unit) ? undefined : toDisplayUnit(unit);
  }, [value, unit]);

  const sx = useObjectMemo({
    paddingLeft: indentation * 2,
    paddingRight: 0,
    ...(marginTop !== undefined ? { marginTop } : {}),
  });

  const hasChildren = React.Children.count(children) > 0;

  // Check if this field has an error
  const hasError = Boolean(error);

  // Create error tooltip message
  const errorTooltip = error ? `⚠️ ${error}` : undefined;

  // Combine original tooltip with error message
  const combinedTooltip = [tooltip, errorTooltip].filter(guards.is.defined);
  const tooltipContent = combinedTooltip.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
      {combinedTooltip.map((item) => (
        <span key={item}>{item}</span>
      ))}
    </div>
  ) : undefined;

  return (
    <>
      <Tooltip key={`tooltip-${name}`} title={tooltipContent} placement="left">
        <ListItem
          sx={sx}
          className={classes.listItem}
          onClick={hasChildren ? toggleExpanded : undefined}
        >
          <ListItemText primary={name} className={classes.nameContainer} />

          {/* Right side */}
          <div className={classes.rightContainer}>
            {/* Error indicator */}
            {hasError && <Warning className={classes.errorIcon} />}

            {/* Value (if defined) */}
            {value !== undefined && (
              <>
                {guards.is.number(value) ? (
                  <ExpressionTypography
                    value={value}
                    unit={expressionTypographyUnit}
                    displayUnit={expressionTypographyDisplayUnit}
                    variant="body2"
                  />
                ) : (
                  <Typography
                    variant="body2"
                    className={highlight ? classes.highlight : undefined}
                    sx={{
                      pr: 2,
                    }}
                  >
                    {value}
                  </Typography>
                )}
              </>
            )}

            {/* Expand button (if has children) */}
            {hasChildren ? (
              <IconButton size="small" className={classes.expandButton}>
                <ArrowDropDown className={isExpanded ? classes.expanded : ''} />
              </IconButton>
            ) : (
              <div className={classes.expandButton} />
            )}
          </div>
        </ListItem>
      </Tooltip>

      {/* Optinally show children */}
      {hasChildren && (
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          {children}
        </Collapse>
      )}
    </>
  );
};

const useStyles = makeStyles()(({ typography, spacing }) => ({
  listItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing(2),
    marginRight: spacing(-20),
  },
  highlight: {
    fontWeight: typography.fontWeightBold,
  },
  errorIcon: {
    fontSize: spacing(2.5),
    marginRight: spacing(0.5),
  },
  expandButton: {
    padding: spacing(0.5),
    marginLeft: 0,
    width: spacing(5),
    height: spacing(5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s',
  },
  expanded: {
    transform: 'rotate(180deg)',
    transformOrigin: '50% 50%',
    display: 'inline-block',
    opacity: 1,
  },
  nameContainer: {
    flex: 1,
  },
  rightContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(0),
    marginRight: spacing(-2),
  },
}));
